import * as CL from '@design-system/component-library';
import { type JSX, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { dsClass } from '../../common/constants/dsClasses.js';
import { getElisaIdChangePasswordUrl } from '../../common/constants/pathVariables.js';
import { DialogType } from '../../common/enums.js';
import {
  cancelMsg,
  changePasswordMsg,
  confirmMsg,
  costCenterAndReferenceMsg,
  costCenterToolTipMsg,
  editMsg,
  emailAddressMsg,
  employeeNumberMsg,
  firstNameMsg,
  invalidCostCenterNumberMsg,
  lastNameMsg,
  mobilePhoneNumberMsg,
  myAccountMsg,
  referenceMsg,
  referenceToolTipMsg,
  t,
} from '../../common/i18n/index.js';
import { Email, LanguageDropdown, Name, PhoneNumber, TextInput } from '../../common/react-hook-form/fields/index.js';
import { convertStringMapToCommonErrors } from '../../common/utils/errorUtils.js';
import { deepEqual } from '../../common/utils/objectUtils.js';
import { PHONE_NUMBER_REGEX } from '../../common/utils/phoneNumberUtils.js';
import { getContactInfo } from '../../common/utils/stateUtils.js';
import { containsYttSubscription } from '../../common/utils/subscriptionUtils.js';
import { COST_CENTER_REGEX } from '../../common/utils/validationUtils.js';
import { AuthenticatedUserRole, CompanyInfoResponse } from '../../generated/api/models.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { useConfig } from '../../public/site/ConfigProvider.js';
import { openDialog } from '../../public/site/dialogStore.js';
import { resendEmailVerificationLink } from '../../selfservice/actions/index.js';
import { isUserDetailsMissing } from '../EoeOutlet/EoeOutlet.js';
import { HeroHeading, HeroHeadingType } from '../HeroHeading/index.js';
import { LinkableAccordion } from '../LinkableAccordion/index.js';
import { Loading } from '../Loading/index.js';
import type { DialogParams, GenericInfoDialogParams } from '../../common/types/dialog.js';
import type { ContactPerson } from '../../generated/api/models.js';
import type { State } from '../../selfservice/common/store.js';

import './UserDetails.scss';

const isMobileIdLogin = (userName: string) => PHONE_NUMBER_REGEX.test(userName);

const formatAsParagraphs = (text: string, parentClass?: string, paragraphClass?: string): JSX.Element => {
  return (
    <div className={parentClass}>
      {text.split('\n').map((item, i) => (
        <div key={i} className={paragraphClass}>
          {item}
        </div>
      ))}
    </div>
  );
};

const getEmailVerificationInfoDialog = (email: string): GenericInfoDialogParams => ({
  body: formatAsParagraphs(
    t.BJ43(
      'Confirmation link was sent to your email. {} and subject as "Welcome to Employee OmaElisa". Also check for junk/spam folders. \n Click "Confirm" to verify you email address. After verification you can start using Elisa Employee portal.',
      email
    )
  ),
  header: t.RXQO('Confirm your email address.'),
  type: DialogType.GENERIC_INFO_DIALOG,
});

interface ContactDetailsValues {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  identityProviderEmail?: string;
  identityProviderPhone?: string;
  costCenter?: string;
  employeeNumber?: string;
}

interface ContactDetailsFormProps {
  showUpdateUserInfoHint: boolean;
  isYtt?: boolean;
  resendLink?: JSX.Element;
  companyInfo?: CompanyInfoResponse;
}

const EmployeeContactDetailsForm = ({
  showUpdateUserInfoHint,
  isYtt,
  resendLink,
  companyInfo,
}: ContactDetailsFormProps) => {
  const { authenticatedUser, updateUserDetails } = useAuth();
  const [isEdit, setIsEdit] = useState(isUserDetailsMissing(authenticatedUser));
  const methods = useForm<ContactDetailsValues>({
    defaultValues: {
      email: authenticatedUser?.email ?? '',
      firstName: authenticatedUser?.firstName ?? '',
      lastName: authenticatedUser?.lastName ?? '',
      phoneNumber: authenticatedUser?.mobile ?? '',
      identityProviderEmail: authenticatedUser?.identityProviderEmail ?? '',
      identityProviderPhone: authenticatedUser?.identityProviderPhone ?? '',
      costCenter: authenticatedUser?.costCenter ?? '',
      employeeNumber: authenticatedUser?.employeeNumber ?? '',
    },
  });
  const onShowDialog = (params: DialogParams) => openDialog(params);
  const { handleSubmit, getValues } = methods;
  const isPunchOutUser = authenticatedUser?.userRole === AuthenticatedUserRole.PUNCHOUT_USER;
  const customerType = companyInfo?.customerType;
  const isCostCenterMandatory = customerType === CompanyInfoResponse.CustomerTypeEnum.INTERNAL_CUSTOMERS;

  const onSubmit = (values: ContactDetailsValues, validationErrors?: { [s: string]: string }) => {
    if (authenticatedUser?.emailVerified === false && !isPunchOutUser) {
      openDialog(getEmailVerificationInfoDialog(values.email));
    }
    const nameEmailOrPhoneChanged =
      authenticatedUser?.firstName !== values.firstName ||
      authenticatedUser?.lastName !== values.lastName ||
      authenticatedUser?.email !== values.email ||
      authenticatedUser?.mobile !== values.phoneNumber;
    if (isYtt && nameEmailOrPhoneChanged) {
      onShowDialog({
        values: values as ContactPerson,
        validationErrors,
        onConfirm: () => {
          setIsEdit(false);
        },
        type: DialogType.UPDATE_USER_YTT_WARNING,
      });
      return;
    }
    updateUserDetails(
      { ...getContactInfo(authenticatedUser!), ...values },
      customerType,
      convertStringMapToCommonErrors(validationErrors)
    );
    setIsEdit(false);
    return true;
  };

  return (
    <FormProvider {...methods}>
      <CL.Grid className={dsClass.MARGIN_TOP_4}>
        <CL.GridRow>
          <CL.GridCol colWidthXS={4} colWidthS={3} colWidthL={6}>
            <Name
              readonly={!isEdit && !isPunchOutUser}
              name="firstName"
              label={t.AIK7(firstNameMsg)}
              placeholder={t.AIK7(firstNameMsg)}
              maxLength={40}
            />
          </CL.GridCol>
          <CL.GridCol colWidthXS={4} colWidthS={3} colWidthL={6}>
            <PhoneNumber
              readonly={!isEdit}
              name="phoneNumber"
              label={`${t.FRYN(mobilePhoneNumberMsg)} (${t.RREM('work')})`}
            />
          </CL.GridCol>
        </CL.GridRow>
        <CL.GridRow>
          <CL.GridCol colWidthXS={4} colWidthS={3} colWidthL={6}>
            <Name
              readonly={!isEdit && !isPunchOutUser}
              name="lastName"
              label={t.Y41S(lastNameMsg)}
              placeholder={t.Y41S(lastNameMsg)}
              maxLength={100}
            />
          </CL.GridCol>
          <CL.GridCol colWidthXS={4} colWidthS={3} colWidthL={6}>
            <Email
              readonly={!isEdit && !isPunchOutUser}
              name="email"
              label={`${t.OKYY(emailAddressMsg)} (${t.RREM('work')})`}
              hint={isEdit ? resendLink : undefined}
            />
          </CL.GridCol>
        </CL.GridRow>
        {authenticatedUser?.businessId && !isPunchOutUser && (
          <CL.GridRow>
            <CL.GridCol colWidthXS={4} colWidthS={3} colWidthL={6}>
              <TextInput
                readonly={!isEdit}
                name="costCenter"
                required={isCostCenterMandatory}
                maxLength={isCostCenterMandatory ? 4 : 40}
                label={t.TEYY(costCenterAndReferenceMsg)}
                placeholder={t.TEYY(costCenterAndReferenceMsg)}
                tooltip={t.UE8R(costCenterToolTipMsg)}
                validate={(value: string) => {
                  const validate = (v: string) => (isCostCenterMandatory ? COST_CENTER_REGEX.test(v) : v.length <= 40);
                  if (!validate(value)) {
                    return t.JE8S(invalidCostCenterNumberMsg);
                  }
                  return undefined;
                }}
              />
            </CL.GridCol>
            <CL.GridCol colWidthXS={4} colWidthS={3} colWidthL={6}>
              <TextInput
                readonly={!isEdit}
                name="employeeNumber"
                required={false}
                label={t.LVRN(referenceMsg)}
                placeholder={t.LVRN(referenceMsg)}
                tooltip={t.HBBD(referenceToolTipMsg)}
                hint={isEdit ? t.FKO8(employeeNumberMsg) : undefined}
                maxLength={80}
              />
            </CL.GridCol>
          </CL.GridRow>
        )}
        {!showUpdateUserInfoHint && !isPunchOutUser && (
          <CL.GridRow>
            <CL.GridCol colWidthXS={4}>
              {formatAsParagraphs(
                t.S0IN(`It is important that your work email address and phone number are up-to-date
              in Employee OmaElisa. We use these contact details when we inform you about Elisa's services that your
              company has acquired. For example, we send you updates when we deliver products you have ordered.\nYour
              company's main user sees your contact details in OmaElisa for companies. There he/she can also make
              updates to your contact details if needed. If your phone number is public, it will also be visible in
              number directory.`)
              )}
            </CL.GridCol>
          </CL.GridRow>
        )}
        <CL.GridRow className={dsClass.MARGIN_TOP_7}>
          <CL.GridCol colWidthXS={4}>
            {isEdit ? (
              <>
                <CL.Button type="submit" onClick={handleSubmit(() => onSubmit(getValues()))}>
                  {t.QVYK(confirmMsg)}
                </CL.Button>
                <CL.Button
                  type="button"
                  color="link"
                  className={dsClass.MARGIN_LEFT_4}
                  onClick={() => setIsEdit(false)}
                >
                  {t.B2V1(cancelMsg)}
                </CL.Button>
              </>
            ) : (
              <CL.Button type="button" color="light" onClick={() => setIsEdit(true)}>
                {t.NVPK(editMsg)}
              </CL.Button>
            )}
          </CL.GridCol>
        </CL.GridRow>
      </CL.Grid>
    </FormProvider>
  );
};

interface LanguageFormValues {
  language: string;
}

const LanguageForm = () => {
  const { authenticatedUser, updateUserDetails } = useAuth();
  const [isEdit, setIsEdit] = useState(false);
  const methods = useForm<LanguageFormValues>({
    defaultValues: {
      language: authenticatedUser?.preferredLanguage?.toUpperCase() || 'FI',
    },
  });
  const { handleSubmit, getValues } = methods;
  const onSubmit = (values: { language: string }) => {
    updateUserDetails({ ...getContactInfo(authenticatedUser!), preferredLanguage: values.language });
    setIsEdit(false);
    return true;
  };

  return (
    <FormProvider {...methods}>
      <CL.Grid>
        {isEdit && (
          <CL.GridRow>
            <CL.GridCol colWidthXS={4} colWidthS={3} colWidthL={6}>
              <LanguageDropdown name="language" />
            </CL.GridCol>
          </CL.GridRow>
        )}
        <CL.GridRow>
          <CL.GridCol colWidthXS={4} colWidthS={6} colWidthL={12}>
            {t.URAQ('The choice affects which language you get e.g. order confirmations and invoices')}
          </CL.GridCol>
        </CL.GridRow>
        <CL.GridRow className={dsClass.MARGIN_TOP_7}>
          <CL.GridCol colWidthXS={4}>
            {isEdit ? (
              <>
                <CL.Button type="submit" onClick={handleSubmit(() => onSubmit(getValues()))}>
                  {t.QVYK(confirmMsg)}
                </CL.Button>
                <CL.Button
                  type="button"
                  color="link"
                  className={dsClass.MARGIN_LEFT_4}
                  onClick={() => setIsEdit(false)}
                >
                  {t.B2V1(cancelMsg)}
                </CL.Button>
              </>
            ) : (
              <CL.Button type="button" color="light" onClick={() => setIsEdit(true)}>
                {t.NVPK(editMsg)}
              </CL.Button>
            )}
          </CL.GridCol>
        </CL.GridRow>
      </CL.Grid>
    </FormProvider>
  );
};

const ChangePasswordDisclaimer = () => {
  const { consumerMobiilivarmenneUrl } = useConfig();
  if (!consumerMobiilivarmenneUrl) {
    return null;
  }

  const changePasswordUrl = getElisaIdChangePasswordUrl(consumerMobiilivarmenneUrl);

  return (
    <CL.Disclaimer
      disclaimerType="default"
      icon={<CL.Icon icon="information" type="regular" aria-hidden="true" />}
      className={dsClass.MARGIN_HORIZONTAL_3}
      buttons={[
        <CL.Button
          key="change-password"
          color="primary"
          iconPosition="right"
          size="s"
          icon={<CL.Icon icon="external-link" color="white" aria-hidden="true" />}
          onClick={() => window.open(changePasswordUrl, '_blank', 'noopener')}
        >
          {t.R2MM(changePasswordMsg)}
        </CL.Button>,
      ]}
      visible
    >
      <h4 className={`${dsClass.TEXT_M} ${dsClass.FONT_WEIGHT_600}`}>
        {t.IAGQ('Your password is managed on a separate password management page.')}
      </h4>
      <p className={`${dsClass.TEXT_S} ${dsClass.FONT_WEIGHT_400}`}>
        {t.LW7P('To change it, open the password management page using the button below.')}
      </p>
      <p className={`${dsClass.TEXT_S} ${dsClass.FONT_WEIGHT_400}`}>
        {t.QJ7O(
          'After updating the password, you will be logged out of all Elisa services and you need to sign in again.'
        )}
      </p>
    </CL.Disclaimer>
  );
};

interface UserDetailsProps {
  companyInfo?: CompanyInfoResponse;
}

export const UserDetails = ({ companyInfo }: UserDetailsProps) => {
  const { authenticatedUser } = useAuth();

  const isPunchoutUser = authenticatedUser?.userRole === AuthenticatedUserRole.PUNCHOUT_USER;
  const showUpdateUserInfoHint = isUserDetailsMissing(authenticatedUser);
  const dispatch = useDispatch();

  useEffect(() => {
    if (authenticatedUser?.email && authenticatedUser?.emailVerified === false) {
      openDialog(getEmailVerificationInfoDialog(authenticatedUser?.email));
    }
  }, [authenticatedUser?.email, authenticatedUser?.emailVerified]);

  const resendLink =
    authenticatedUser?.email && authenticatedUser?.emailVerified === false ? (
      <CL.Button
        type="button"
        color="link"
        className={dsClass.MARGIN_TOP_2}
        onClick={() => dispatch(resendEmailVerificationLink())}
      >
        {t.MLUL('Resend confirmation link')}
      </CL.Button>
    ) : undefined;

  const notification = t.S0IN(
    `It is important that your work email address and phone number are up-to-date in Employee OmaElisa. We use these contact details when we inform you about Elisa's services that your company has acquired. For example, we send you updates when we deliver products you have ordered.\nYour company's main user sees your contact details in OmaElisa for companies. There he/she can also make updates to your contact details if needed. If your phone number is public, it will also be visible in number directory.`
  );

  const serviceSubscriptions = useSelector(
    (state: State) => state.user?.authenticated?.subscriptions?.service,
    deepEqual
  );

  const content = (
    <div className="of-user-details__content-bottom">
      <LinkableAccordion defaultOpen={true} heading={t.SEBD(myAccountMsg)} headingLevel="h3" id="my-account">
        {showUpdateUserInfoHint && (
          <CL.Disclaimer title={t.JE74('Remember to check your contact details')} visible>
            {formatAsParagraphs(notification, undefined, `${dsClass.MARGIN_TOP_3} ${dsClass.MARGIN_BOTTOM_2}`)}
          </CL.Disclaimer>
        )}
        <EmployeeContactDetailsForm
          showUpdateUserInfoHint={showUpdateUserInfoHint}
          isYtt={containsYttSubscription(serviceSubscriptions)}
          resendLink={resendLink}
          companyInfo={companyInfo}
        />
      </LinkableAccordion>
      {authenticatedUser?.businessId && !isPunchoutUser && (
        <LinkableAccordion heading={t.IL1L('Language')} headingLevel="h3" id="language">
          <LanguageForm />
        </LinkableAccordion>
      )}
      {authenticatedUser?.userName && !isMobileIdLogin(authenticatedUser.userName) && !isPunchoutUser && (
        <LinkableAccordion heading={t.R2MM(changePasswordMsg)} headingLevel="h3" id="change-password">
          <ChangePasswordDisclaimer />
        </LinkableAccordion>
      )}
    </div>
  );

  if (
    authenticatedUser?.identityProviderEmail === undefined &&
    authenticatedUser?.identityProviderPhone === undefined &&
    !isPunchoutUser
  ) {
    return <Loading big={true} bottomPadding={4} />;
  }

  return (
    <div className="of-user-details">
      <HeroHeading
        center={true}
        fullScreen={true}
        heroHeadingType={HeroHeadingType.EMPLOYEE_PROFILE}
        title={authenticatedUser?.firstName ?? ''}
        subtitle={authenticatedUser ? authenticatedUser.companyName : undefined}
      />
      <div className="content">
        <div className="for-margin">
          <div className="for-desktop-margin">
            <CL.Grid>
              <CL.GridRow>
                <CL.GridCol colWidthXS={4} colWidthS={6} colWidthL={11}>
                  <div className={dsClass.PADDING_TOP_7}>{content}</div>
                </CL.GridCol>
              </CL.GridRow>
            </CL.Grid>
          </div>
        </div>
      </div>
    </div>
  );
};
